import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createUser, createAuthUser, fetchPartners } from "api";
import { PageHeader, FormField, Spinner, Select } from "components/common";
import type { Role } from "types";

interface FormState {
  partner_entity_id: string;
  name_1:            string;
  name_2:            string;
  email:             string;
  phone:             string;
  title:             string;
  role:              Role | "";
}

const INITIAL: FormState = {
  partner_entity_id: "",
  name_1:            "",
  name_2:            "",
  email:             "",
  phone:             "",
  title:             "",
  role:              "",
};

const ROLES: { value: Role; label: string }[] = [
  { value: "bridge13_admin",   label: "Admin"   },
  { value: "bridge13_courier", label: "Courier" },
  { value: "partner",          label: "Partner" },
];

export default function NewUserForm() {
  const navigate       = useNavigate();
  const queryClient    = useQueryClient();
  const [searchParams] = useSearchParams();

  const [form, setForm] = useState<FormState>({
    ...INITIAL,
    partner_entity_id: searchParams.get("partner_entity_id") ?? "",
  });

  const set = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const { data: partners = [] } = useQuery({
    queryKey: ["partners"],
    queryFn:  fetchPartners,
  });

  const { mutate, isPending, error } = useMutation({
    mutationFn: async () => {
      const user = await createUser({
        partner_entity_id: Number(form.partner_entity_id),
        name_1:            form.name_1.trim(),
        name_2:            form.name_2.trim() || undefined,
        email:             form.email.trim()  || undefined,
        phone:             form.phone.trim()  || undefined,
        title:             form.title.trim()  || undefined,
      });
      if (form.role) {
        await createAuthUser(user.id, form.email.trim(), form.role);
      }
      return user;
    },
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      navigate("/users/" + user.id);
    },
  });

  const roleRequiresEmail = !!form.role && !form.email.trim();
  const isValid = form.name_1.trim() && form.partner_entity_id && !roleRequiresEmail;

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8 max-w-lg">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-gray-400 hover:text-gray-600 mb-6 transition-colors"
      >
        ← Back
      </button>

      <PageHeader title="Add new user" subtitle="Create a contact user for a partner." />

      <form
        onSubmit={(e) => { e.preventDefault(); if (isValid) mutate(); }}
        className="card p-6 space-y-5"
      >
        <FormField label="Partner">
          <Select value={form.partner_entity_id} onChange={set("partner_entity_id")}>
            <option value="" disabled>Select a partner…</option>
            {partners.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          <FormField label="First name">
            <input
              className="input"
              value={form.name_1}
              onChange={set("name_1")}
              placeholder="Jane"
              required
              autoFocus
            />
          </FormField>

          <FormField label="Last name">
            <input
              className="input"
              value={form.name_2}
              onChange={set("name_2")}
              placeholder="Smith"
            />
          </FormField>
        </div>

        <FormField label="Email">
          <input
            className="input"
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="jane@example.com"
          />
        </FormField>

        <FormField label="Phone">
          <input
            className="input"
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            placeholder="+1 (555) 000-0000"
          />
        </FormField>

        <FormField label="Title">
          <input
            className="input"
            value={form.title}
            onChange={set("title")}
            placeholder="e.g. Operations Manager"
          />
        </FormField>

        <FormField label="Login role">
          <Select value={form.role} onChange={set("role")}>
            <option value="">— No login access —</option>
            {ROLES.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
          {roleRequiresEmail && (
            <p className="text-xs text-amber-600 mt-1">Email is required to assign a role.</p>
          )}
        </FormField>

        {error && (
          <p className="text-sm text-red-500">Something went wrong. Please try again.</p>
        )}

        <div className="flex gap-3 pt-2">
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={!isValid || isPending}>
            {isPending ? <Spinner className="h-4" /> : "Create user"}
          </button>
        </div>
      </form>
    </div>
  );
}
